import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Palette, Eye, EyeOff } from 'lucide-react';

function formatApiErrorDetail(detail) {
  if (detail == null) return "An error occurred. Please check your connection and try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function RegisterPage() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      await register(email, password, name);
      navigate('/');
    } catch (err) {
      console.error('Register error:', err);
      if (err.response?.data?.detail) {
        setError(formatApiErrorDetail(err.response.data.detail));
      } else if (err.message === 'Network Error') {
        setError("Unable to connect to server. Please check your connection and try again.");
      } else if (err.message) {
        setError(err.message);
      } else {
        setError("Registration failed: " + String(err));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left side - Register Form */}
      <div className="flex items-center justify-center p-6 md:p-12 bg-[#FAFAFA]">
        <Card className="w-full max-w-md border-slate-200 shadow-sm rounded-xl">
          <CardHeader className="space-y-1 text-center">
            <div className="flex justify-center mb-4">
              <div className="p-3 rounded-xl bg-blue-600">
                <Palette className="h-8 w-8 text-white" />
              </div>
            </div>
            <CardTitle className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Create your account
            </CardTitle>
            <CardDescription className="text-slate-500" style={{ fontFamily: 'Figtree, sans-serif' }}>
              Join Create(ED) and start organizing your art classroom
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm" data-testid="register-error">
                  {error}
                </div>
              )}
              
              <div className="space-y-2">
                <Label htmlFor="name" className="text-slate-700" style={{ fontFamily: 'Figtree, sans-serif' }}>
                  Full Name
                </Label>
                <Input
                  id="name"
                  type="text"
                  placeholder="Ms. Johnson"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  data-testid="register-name-input"
                  className="bg-slate-50 border-slate-200 rounded-md focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email" className="text-slate-700" style={{ fontFamily: 'Figtree, sans-serif' }}>
                  Email
                </Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="teacher@school.edu"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="register-email-input"
                  className="bg-slate-50 border-slate-200 rounded-md focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="password" className="text-slate-700" style={{ fontFamily: 'Figtree, sans-serif' }}>
                  Password
                </Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    data-testid="register-password-input"
                    className="bg-slate-50 border-slate-200 rounded-md pr-10 focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    data-testid="toggle-password-visibility"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="text-slate-700" style={{ fontFamily: 'Figtree, sans-serif' }}>
                  Confirm Password
                </Label>
                <Input
                  id="confirmPassword"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  data-testid="register-confirm-password-input"
                  className="bg-slate-50 border-slate-200 rounded-md focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                />
              </div>
              
              <Button
                type="submit"
                disabled={loading}
                data-testid="register-submit-button"
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg py-2 transition-colors"
              >
                {loading ? 'Creating account...' : 'Create account'}
              </Button>
            </form>
            
            <div className="mt-6 text-center">
              <p className="text-sm text-slate-500" style={{ fontFamily: 'Figtree, sans-serif' }}>
                Already have an account?{' '}
                <Link to="/login" className="text-blue-600 hover:text-blue-700 font-medium" data-testid="go-to-login">
                  Sign in
                </Link>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Right side - Image */}
      <div
        className="hidden lg:block bg-cover bg-center relative"
        style={{
          backgroundImage: 'url(https://images.pexels.com/photos/1821377/pexels-photo-1821377.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940)'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-blue-600/40 to-amber-500/30" />
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <h2 className="text-3xl font-bold mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Create(ED)
          </h2>
          <p className="text-lg opacity-90" style={{ fontFamily: 'Figtree, sans-serif' }}>
            The art teacher workspace for Preschool through High School. Plan lessons, align standards, organize student artwork, and manage your classroom workflow.
          </p>
        </div>
      </div>
    </div>
  );
}
